/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useContext, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  Star,
  ChevronDown,
  ChevronUp,
  Check,
  X,
  Phone,
  Search,
  Filter,
  RefreshCw,
  Calendar,
  Briefcase,
  Loader2,
} from "lucide-react";
import { AdminContext } from "../../../context/AdminContext";
import Pagination from "../../../components/Pagination";
import { toast } from "react-toastify";

// Helper to format date
const formatDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  const now = new Date();
  const diffInHours = (now - date) / (1000 * 60 * 60);

  if (diffInHours < 1) return "Just now";
  if (diffInHours < 24) return `${Math.floor(diffInHours)}h ago`;
  if (diffInHours < 48) return "Yesterday";

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const JobApplicants = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { getApplicants, updateApplicationStatus } = useContext(AdminContext);
  const [applicants, setApplicants] = useState([]);
  const [expandedRow, setExpandedRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingId, setProcessingId] = useState(null); // Track which applicant is being processed

  // Filter States
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, setStatusFilter] = useState("All");
  const [genderFilter, setGenderFilter] = useState("All");
  const [experienceFilter, setExperienceFilter] = useState("All");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  // Fetch applicants function
  const fetchApplicants = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getApplicants(jobId);
      // Map ALL applicants including those with active work
      setApplicants(
        data.map((app) => ({
          id: app.id,
          caregiverId: app.applicant?.id,
          name: app.applicant?.name || "Unknown",
          avatar:
            app.applicant?.image ||
            `https://ui-avatars.com/api/?name=${encodeURIComponent(app.applicant?.name || "Unknown")}&background=random`,
          phone: app.applicant?.phoneNumber || "N/A",
          email: app.applicant?.email || "N/A",
          appliedDate: formatDate(app.createdAt),
          rawStatus: app.status,
          status: app.status.charAt(0).toUpperCase() + app.status.slice(1),
          fees: app.applicant?.fees || 0,
          rating: app.applicant?.rating || 0,
          experience: app.applicant?.experience || "N/A",
          nationality: app.applicant?.nationality || "N/A",
          languages: app.applicant?.languages || [],
          gender: app.applicant?.gender || "Not Selected",
          about: app.applicant?.about || "No description available",
          workPreferences: app.applicant?.workTimePreference || "Not specified",
          onboarded: app.applicant?.vettingStatus === "Onboarded",
          hasActiveJob: Array.isArray(app.applicant?.assignedJobs)
            ? app.applicant.assignedJobs.length > 0
            : false,
          hasActiveAppointment: Array.isArray(app.applicant?.bookings)
            ? app.applicant.bookings.length > 0
            : false,
          vettingStatus: app.applicant?.vettingStatus || "Unknown",
          // Dynamic field values
          fieldValues: app.applicant?.fieldValues || [],
          serviceCategory: app.applicant?.serviceCategory || null,
        })),
      );
    } catch (err) {
      toast.error("Failed to load applicants");
    } finally {
      setLoading(false);
    }
  }, [jobId, getApplicants]);

  useEffect(() => {
    fetchApplicants();
  }, [fetchApplicants]);

  // Filter Logic
  const filteredApplicants = applicants.filter((app) => {
    const matchesSearch =
      app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      app.phone.includes(searchTerm);
    const matchesStatus = statusFilter === "All" || app.status === statusFilter;
    const matchesGender = genderFilter === "All" || app.gender === genderFilter;
    const matchesExperience =
      experienceFilter === "All" ||
      (app.experience &&
        app.experience.toString().includes(experienceFilter.split(" ")[0]));

    return matchesSearch && matchesStatus && matchesGender && matchesExperience;
  });

  const paginatedApplicants = filteredApplicants.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );
  const totalPages = Math.ceil(filteredApplicants.length / itemsPerPage);

  // Reset pagination on filter change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, genderFilter, experienceFilter, itemsPerPage]);

  const toggleExpand = (applicantId) => {
    setExpandedRow(expandedRow === applicantId ? null : applicantId);
  };

  const handleAction = async (applicantId, action) => {
    // Prevent multiple clicks
    if (processingId) return;

    setProcessingId(applicantId); // Set processing state

    try {
      await updateApplicationStatus(applicantId, action.toLowerCase());

      // Refresh the applicants list to get updated statuses
      await fetchApplicants();

      toast.success(`Application ${action.toLowerCase()} successfully`);

      // Collapse expanded row if open
      if (expandedRow === applicantId) {
        setExpandedRow(null);
      }
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message;

      if (errorMessage.includes("onboarded")) {
        toast.error("Only onboarded caregivers can be accepted.");
      } else if (
        errorMessage.includes("currentJob") ||
        errorMessage.includes("active job")
      ) {
        toast.error("Caregiver has an active job and cannot be accepted.");
      } else if (errorMessage.includes("appointment")) {
        toast.error("Client has an active appointment in this category.");
      } else {
        toast.error(errorMessage || "Failed to update application status");
      }
    } finally {
      setProcessingId(null);
    }
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    setStatusFilter("All");
    setGenderFilter("All");
    setExperienceFilter("All");
  };

  // Status badge style helper
  const getStatusStyle = (status) => {
    switch (status.toLowerCase()) {
      case "accepted":
        return "bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/20 dark:text-emerald-400 dark:border-emerald-800";
      case "rejected":
        return "bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800";
      case "under-review":
        return "bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-400 dark:border-blue-800";
      case "withdrawn":
        return "bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-800 dark:text-gray-400 dark:border-gray-600";
      default:
        return "bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800";
    }
  };

  return (
    <div className="animate-in fade-in slide-in-from-right-4 duration-500 pb-12">
      {/* Header */}
      <div className="flex items-center gap-4 mb-8">
        <button
          onClick={() => navigate("/requests/manage")}
          className="p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors text-text dark:text-dark-text"
        >
          <ChevronLeft size={24} />
        </button>
        <div>
          <h1 className="text-3xl font-bold text-text dark:text-dark-text">
            Job Applicants
          </h1>
          <p className="text-gray-500 dark:text-dark-muted mt-1">
            Managing {filteredApplicants.length} applicant(s) for Request{" "}
            <span className="font-semibold text-gray-700 dark:text-gray-300">
              #{jobId}
            </span>
          </p>
        </div>
      </div>

      {/* Filter Section  */}
      <div className="bg-white dark:bg-dark-surface p-4 rounded-xl shadow-sm border border-gray-200 dark:border-dark-border mb-6">
        {/* Top Row: Search & Toggles */}
        <div className="flex flex-col md:flex-row gap-4 justify-between items-center mb-2">
          <div className="relative w-full md:flex-1">
            <Search
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
              size={20}
            />
            <input
              type="text"
              placeholder="Search by applicant name or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-dark-border outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-gray-700 text-text dark:text-dark-text placeholder-gray-400 font-medium transition-all"
            />
          </div>
          <div className="flex gap-3 w-full md:w-auto">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex-1 md:flex-none flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold transition-all border ${
                showFilters
                  ? "bg-gray-100 text-text dark:bg-gray-800 dark:text-dark-text border-gray-300 dark:border-gray-600"
                  : "bg-white text-gray-600 dark:bg-dark-surface dark:text-dark-muted border-gray-200 dark:border-dark-border hover:bg-gray-50 dark:hover:bg-gray-800"
              }`}
            >
              <Filter size={18} />
              <span>{showFilters ? "Hide Filters" : "Show Filters"}</span>
            </button>
            {showFilters && (
              <button
                onClick={handleResetFilters}
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400 font-semibold hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors border border-gray-200 dark:border-dark-border"
                title="Reset Filters"
              >
                <RefreshCw size={18} />
              </button>
            )}
          </div>
        </div>

        {/* Collapsible Filters */}
        <div
          className={`grid grid-cols-1 sm:grid-cols-3 gap-4 overflow-hidden transition-all duration-300 ease-in-out ${
            showFilters
              ? "max-h-40 opacity-100 mt-4 pt-4 border-t border-gray-200 dark:border-dark-border"
              : "max-h-0 opacity-0"
          }`}
        >
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-400 dark:text-dark-muted uppercase tracking-wider ml-1">
              Status
            </label>
            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full appearance-none pl-4 pr-10 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-dark-border text-sm font-medium text-text dark:text-dark-text focus:ring-2 focus:ring-gray-200 dark:focus:ring-gray-700 outline-none cursor-pointer"
              >
                <option value="All">All Statuses</option>
                <option value="Pending">Pending</option>
                <option value="Accepted">Accepted</option>
                <option value="Rejected">Rejected</option>
                <option value="Under-review">Under Review</option>
              </select>
              <ChevronDown
                size={14}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-400 dark:text-dark-muted uppercase tracking-wider ml-1">
              Experience
            </label>
            <div className="relative">
              <select
                value={experienceFilter}
                onChange={(e) => setExperienceFilter(e.target.value)}
                className="w-full appearance-none pl-4 pr-10 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-dark-border text-sm font-medium text-text dark:text-dark-text focus:ring-2 focus:ring-gray-200 dark:focus:ring-gray-700 outline-none cursor-pointer"
              >
                <option value="All">All Levels</option>
                <option value="1">1+ Years</option>
                <option value="3">3+ Years</option>
                <option value="5">5+ Years</option>
              </select>
              <ChevronDown
                size={14}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <label className="text-xs font-bold text-gray-400 dark:text-dark-muted uppercase tracking-wider ml-1">
              Gender
            </label>
            <div className="relative">
              <select
                value={genderFilter}
                onChange={(e) => setGenderFilter(e.target.value)}
                className="w-full appearance-none pl-4 pr-10 py-2.5 rounded-lg bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-dark-border text-sm font-medium text-text dark:text-dark-text focus:ring-2 focus:ring-gray-200 dark:focus:ring-gray-700 outline-none cursor-pointer"
              >
                <option value="All">All Genders</option>
                <option value="Female">Female</option>
                <option value="Male">Male</option>
              </select>
              <ChevronDown
                size={14}
                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-dark-surface rounded-xl shadow-sm border border-gray-200 dark:border-dark-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="text-xs text-gray-500 dark:text-dark-muted uppercase bg-gray-50 dark:bg-gray-900/50">
              <tr>
                <th className="px-6 py-4 font-bold tracking-wider">
                  Applicant
                </th>
                <th className="px-6 py-4 font-bold tracking-wider">
                  Applied Date
                </th>
                <th className="px-6 py-4 font-bold tracking-wider">
                  Fee Expectation
                </th>
                <th className="px-6 py-4 font-bold tracking-wider">Rating</th>
                <th className="px-6 py-4 font-bold tracking-wider">Status</th>
                <th className="px-6 py-4 font-bold tracking-wider text-center">
                  Actions
                </th>
                <th className="px-6 py-4"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600 dark:border-gray-400"></div>
                    </div>
                  </td>
                </tr>
              ) : paginatedApplicants.length > 0 ? (
                paginatedApplicants.map((app) => {
                  const isProcessing = processingId === app.id;
                  const isAnyProcessing = processingId !== null;
                  const canAccept =
                    app.onboarded &&
                    !app.hasActiveJob &&
                    !app.hasActiveAppointment;

                  return (
                    <React.Fragment key={app.id}>
                      <tr
                        className={`hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${
                          expandedRow === app.id
                            ? "bg-gray-50 dark:bg-gray-800"
                            : ""
                        }`}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <img
                              src={app.avatar}
                              alt={app.name}
                              className="w-10 h-10 rounded-full object-cover"
                            />
                            <div>
                              <p className="font-bold text-text dark:text-dark-text">
                                {app.name}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-dark-muted">
                                {app.email}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-gray-600 dark:text-dark-muted">
                          <div className="flex items-center gap-1">
                            <Calendar
                              size={14}
                              className="text-gray-400 dark:text-gray-500"
                            />
                            <span>{app.appliedDate}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 font-medium text-text dark:text-dark-text">
                          Br {app.fees.toLocaleString()}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-1 text-yellow-500">
                            <Star size={14} fill="currentColor" />
                            <span className="font-bold text-text dark:text-dark-text">
                              {app.rating
                                ? Number(app.rating).toFixed(1)
                                : "0.0"}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span
                            className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium border ${getStatusStyle(
                              app.status,
                            )}`}
                          >
                            {app.status}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex flex-col gap-2">
                            {/* Status Indicators */}
                            {(!app.onboarded ||
                              app.hasActiveJob ||
                              app.hasActiveAppointment) && (
                              <div className="flex flex-col gap-1 mb-1">
                                {!app.onboarded && (
                                  <span className="px-2 py-0.5 text-[10px] font-semibold bg-yellow-50 text-yellow-700 border border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-400 dark:border-yellow-800 rounded text-center">
                                    ⚠️ Not Onboarded ({app.vettingStatus})
                                  </span>
                                )}
                                {app.hasActiveJob && (
                                  <span className="px-2 py-0.5 text-[10px] font-semibold bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800 rounded text-center flex items-center justify-center gap-1">
                                    <Briefcase size={10} /> Has Active Job
                                  </span>
                                )}
                                {app.hasActiveAppointment && (
                                  <span className="px-2 py-0.5 text-[10px] font-semibold bg-orange-50 text-orange-700 border border-orange-200 dark:bg-orange-900/20 dark:text-orange-400 dark:border-orange-800 rounded text-center">
                                    Has Active Work
                                  </span>
                                )}
                              </div>
                            )}

                            {/* Action Buttons */}
                            <div className="flex justify-center gap-2">
                              {app.status === "Pending" && (
                                <>
                                  <button
                                    onClick={() =>
                                      handleAction(app.id, "Accepted")
                                    }
                                    disabled={!canAccept || isAnyProcessing}
                                    className={`p-2 rounded-lg border transition-colors relative ${
                                      !canAccept || isAnyProcessing
                                        ? "bg-gray-100 text-gray-400 border-gray-200 dark:bg-gray-800 dark:border-gray-700 cursor-not-allowed"
                                        : "bg-emerald-50 text-emerald-600 border-emerald-200 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:border-emerald-800 dark:text-emerald-400"
                                    }`}
                                    title={
                                      !app.onboarded
                                        ? "Caregiver must be onboarded first"
                                        : app.hasActiveJob
                                          ? "Caregiver has an active job"
                                          : app.hasActiveAppointment
                                            ? "Caregiver has an active appointment"
                                            : "Accept"
                                    }
                                  >
                                    {isProcessing ? (
                                      <Loader2
                                        size={18}
                                        className="animate-spin"
                                      />
                                    ) : (
                                      <Check size={18} />
                                    )}
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleAction(app.id, "Rejected")
                                    }
                                    disabled={isAnyProcessing}
                                    className={`p-2 rounded-lg border transition-colors relative ${
                                      isAnyProcessing
                                        ? "bg-gray-100 text-gray-400 border-gray-200 dark:bg-gray-800 dark:border-gray-700 cursor-not-allowed"
                                        : "bg-red-50 text-red-600 border-red-200 hover:bg-red-100 dark:bg-red-900/20 dark:border-red-800 dark:text-red-400"
                                    }`}
                                    title="Reject"
                                  >
                                    {isProcessing ? (
                                      <Loader2
                                        size={18}
                                        className="animate-spin"
                                      />
                                    ) : (
                                      <X size={18} />
                                    )}
                                  </button>
                                </>
                              )}
                              {app.status !== "Pending" && (
                                <span className="text-xs text-gray-500 dark:text-dark-muted italic py-2">
                                  {app.status}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            onClick={() => toggleExpand(app.id)}
                            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                          >
                            {expandedRow === app.id ? (
                              <ChevronUp size={20} />
                            ) : (
                              <ChevronDown size={20} />
                            )}
                          </button>
                        </td>
                      </tr>
                      {expandedRow === app.id && (
                        <tr>
                          <td colSpan={7} className="px-0">
                            <div className="bg-gray-50 dark:bg-gray-900/30 p-8 border-y border-gray-200 dark:border-dark-border">
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                {/* Personal Info */}
                                <div className="space-y-4">
                                  <h4 className="font-bold text-text dark:text-dark-text border-b border-gray-200 dark:border-dark-border pb-2">
                                    Personal Info
                                  </h4>
                                  <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div>
                                      <span className="block text-gray-400 dark:text-dark-muted text-xs uppercase mb-1">
                                        Nationality
                                      </span>
                                      <span className="font-medium text-text dark:text-dark-text">
                                        {app.nationality}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="block text-gray-400 dark:text-dark-muted text-xs uppercase mb-1">
                                        Gender
                                      </span>
                                      <span className="font-medium text-text dark:text-dark-text">
                                        {app.gender}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="block text-gray-400 dark:text-dark-muted text-xs uppercase mb-1">
                                        Experience
                                      </span>
                                      <span className="font-medium text-text dark:text-dark-text">
                                        {app.experience} years
                                      </span>
                                    </div>
                                    <div>
                                      <span className="block text-gray-400 dark:text-dark-muted text-xs uppercase mb-1">
                                        Vetting Status
                                      </span>
                                      <span
                                        className={`font-medium ${
                                          app.onboarded
                                            ? "text-emerald-600 dark:text-emerald-400"
                                            : "text-yellow-600 dark:text-yellow-400"
                                        }`}
                                      >
                                        {app.vettingStatus}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="block text-gray-400 dark:text-dark-muted text-xs uppercase mb-1">
                                        Current Status
                                      </span>
                                      <span
                                        className={`font-medium ${
                                          app.hasActiveJob ||
                                          app.hasActiveAppointment
                                            ? "text-red-600 dark:text-red-400"
                                            : "text-emerald-600 dark:text-emerald-400"
                                        }`}
                                      >
                                        {app.hasActiveJob
                                          ? "Has Active Job"
                                          : app.hasActiveAppointment
                                            ? "Has Active Appointment"
                                            : "Available"}
                                      </span>
                                    </div>
                                    <div>
                                      <span className="block text-gray-400 dark:text-dark-muted text-xs uppercase mb-1">
                                        Phone Number
                                      </span>
                                      <div className="flex items-center gap-2">
                                        <Phone
                                          size={14}
                                          className="text-gray-400"
                                        />
                                        <span className="font-medium text-text dark:text-dark-text">
                                          {app.phone}
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                {/* Dynamic Category Fields */}
                                <div className="space-y-4">
                                  <h4 className="font-bold text-text dark:text-dark-text border-b border-gray-200 dark:border-dark-border pb-2">
                                    {app.serviceCategory?.label || "Category"}{" "}
                                    Details
                                  </h4>
                                  {app.fieldValues &&
                                  app.fieldValues.length > 0 ? (
                                    <div className="space-y-3">
                                      {app.fieldValues.map((fv) => (
                                        <div key={fv.id}>
                                          <span className="block text-gray-400 dark:text-dark-muted text-xs uppercase mb-1">
                                            {fv.field?.label || "Field"}
                                          </span>
                                          <div className="font-medium text-text dark:text-dark-text text-sm">
                                            {Array.isArray(fv.values) ? (
                                              fv.values.length > 0 ? (
                                                <div className="flex flex-wrap gap-1">
                                                  {fv.values.map((val, idx) => (
                                                    <span
                                                      key={idx}
                                                      className="px-2 py-0.5 bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded text-xs"
                                                    >
                                                      {val}
                                                    </span>
                                                  ))}
                                                </div>
                                              ) : (
                                                <span className="text-gray-500 dark:text-dark-muted">
                                                  Not specified
                                                </span>
                                              )
                                            ) : (
                                              <span>
                                                {fv.values || "Not specified"}
                                              </span>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <p className="text-sm text-gray-500 dark:text-dark-muted">
                                      No category-specific details available
                                    </p>
                                  )}
                                </div>

                                {/* Skills & Preferences */}
                                <div className="space-y-4">
                                  <h4 className="font-bold text-text dark:text-dark-text border-b border-gray-200 dark:border-dark-border pb-2">
                                    Skills & Preferences
                                  </h4>
                                  <div>
                                    <span className="block text-gray-400 dark:text-dark-muted text-xs uppercase mb-2">
                                      Languages
                                    </span>
                                    <div className="flex flex-wrap gap-2">
                                      {app.languages.length > 0 ? (
                                        app.languages.map((l) => (
                                          <span
                                            key={l}
                                            className="px-2 py-1 bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded text-xs font-medium text-text dark:text-dark-text"
                                          >
                                            {l}
                                          </span>
                                        ))
                                      ) : (
                                        <span className="text-sm text-gray-500 dark:text-dark-muted">
                                          No languages specified
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                  <div>
                                    <span className="block text-gray-400 dark:text-dark-muted text-xs uppercase mt-4 mb-1">
                                      Work Preferences
                                    </span>
                                    <p className="text-sm font-medium text-text dark:text-dark-text">
                                      {app.workPreferences}
                                    </p>
                                  </div>
                                  <div>
                                    <span className="block text-gray-400 dark:text-dark-muted text-xs uppercase mt-4 mb-1">
                                      About
                                    </span>
                                    <p className="text-sm text-gray-600 dark:text-dark-muted leading-relaxed">
                                      {app.about}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              ) : (
                <tr>
                  <td
                    colSpan={7}
                    className="px-6 py-12 text-center text-gray-500 dark:text-dark-muted"
                  >
                    No applicants found matching your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {!loading && filteredApplicants.length > 0 && (
          <div className="border-t border-gray-200 dark:border-dark-border bg-gray-50/50 dark:bg-gray-900/50 p-2">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={setItemsPerPage}
              totalItems={filteredApplicants.length}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default JobApplicants;
